<template>
  <!--
    Public preview product sheet.

    Hero-first layout: a full-bleed image at the top, then attributes +
    modifier groups rendered as INTERACTIVE selectors (radio for single,
    checkbox for multiple, attribute chips light up on select). A sticky
    footer carries the running unit price so visitors can immediately
    see what a given combination would cost.

    No add-to-cart, no quantity stepper — this remains a browsing UI.
    The footer message makes the "preview only" contract explicit.
  -->
  <div class="offcanvas-backdrop fade show" style="z-index: 1054;" @click="$emit('close')" />
  <div ref="sheetWrap" class="sheet-wrap" :style="wrapStyle">
    <!--
      Drag-handle grabber: the visible affordance that the sheet can be
      dragged. Centred so it never overlaps the close button. The sticky
      top bar is ALSO draggable (see onHandleDown) — this is just the cue.
    -->
    <div class="drag-handle" @pointerdown="onHandleDown">
      <span class="drag-handle__bar" />
    </div>
    <div ref="sheet" class="sheet" @scroll="onSheetScroll">
      <!--
        Slim sticky top bar: close button always shows; title + live
        price fade in once the user scrolls past the hero so the photo
        carries the first impression uninterrupted.
      -->
      <!--
        The whole bar is the drag region — dragging it moves the sheet
        with the finger; release snaps to full / peek or dismisses. A
        guard in onHandleDown skips the drag when the pointer starts on
        the close button so tapping it still closes.
      -->
      <div class="sticky-bar" :style="barStyle" @pointerdown="onHandleDown">
        <button class="bar-btn" :style="barBtnStyle" :title="$t('Close')" @click="$emit('close')">
          <i class="fa-solid fa-xmark" />
        </button>
        <div class="bar-title text-truncate" :style="barTitleStyle">
          <template v-if="!loading">
            <span class="fw-bold">{{ $ln(displaySource) }}</span>
            <span class="bar-price ms-2">{{ $storeIndex.formatThaiCurrency(currentUnitPrice) }}</span>
          </template>
        </div>
        <span class="bar-spacer" />
      </div>

      <!-- Loading skeleton -->
      <div v-if="loading">
        <div class="hero hero-skeleton skeleton-shimmer" />
        <div class="p-3">
          <div class="skeleton-shimmer rounded mb-3" style="height: 60px;" />
          <div class="skeleton-shimmer rounded mb-2" style="height: 18px; width: 35%;" />
          <div class="d-flex flex-wrap gap-2 mb-3">
            <div
              v-for="i in 4" :key="'opt-' + i"
              class="skeleton-shimmer rounded-pill" style="height: 38px; width: 80px;"
            />
          </div>
          <div class="skeleton-shimmer rounded mb-2" style="height: 18px; width: 45%;" />
          <div
            v-for="i in 3" :key="'mod-' + i"
            class="skeleton-shimmer rounded-3 mb-2" style="height: 50px;"
          />
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="p-4 text-center">
        <i class="fa-solid fa-circle-exclamation fa-2x text-danger mb-2 opacity-75" />
        <p class="fw-bold mb-1">{{ $t('Failed to load product') }}</p>
        <p class="small text-muted mb-3">{{ error }}</p>
        <button class="btn btn-theme text-white rounded-pill px-4" @click="fetchInfo">
          <i class="fa-solid fa-rotate me-2" />{{ $t('Try again') }}
        </button>
      </div>

      <!-- Content -->
      <template v-else>
        <!-- Hero -->
        <div class="hero">
          <img
            v-if="detail.image_url"
            :src="detail.image_url"
            class="hero-image"
            :style="heroParallaxStyle"
            @error="$storeIndex.onImageError"
          >
          <div v-else class="hero-placeholder" :style="heroParallaxStyle">
            <i class="fa-solid fa-utensils" />
          </div>
          <div class="hero-gradient" />

          <span v-if="detail.category" class="hero-category">
            <i class="fa-solid fa-tag me-1" />{{ $ln(detail, 'category') }}
          </span>

          <div class="hero-overlay text-white">
            <h3 class="hero-title fw-bold mb-2">{{ $ln(displaySource) }}</h3>
            <div class="d-flex flex-wrap align-items-center gap-2">
              <span v-if="compareAtLabel" class="text-decoration-line-through opacity-50 me-2 align-middle" style="font-size: 1rem;">{{ compareAtLabel }}</span>
              <span class="hero-price">{{ priceRangeLabel }}</span>
              <!-- <span v-if="variants.length > 1" class="hero-meta-chip">
                <i class="fa-solid fa-grip me-1" />{{ variants.length }} {{ $t('variants') }}
              </span>
              <span v-if="totalModifierCount > 0" class="hero-meta-chip">
                <i class="fa-solid fa-sliders me-1" />{{ totalModifierCount }} {{ $t('add-ons') }}
              </span> -->
            </div>
          </div>
        </div>

        <div class="content p-3">
          <!-- Preview-only notice -->
          <div class="preview-notice mb-3">
            <i class="fa-solid fa-eye preview-notice__icon" />
            <div class="flex-grow-1">
              <div class="fw-bold">{{ $t('Preview Only') }}</div>
              <div class="small">{{ $t('Try the options below to see the price — ordering is not available.') }}</div>
            </div>
          </div>

          <!-- Attributes (interactive) -->
          <section
            v-for="attribute in attributes"
            :key="'attr-' + attribute.id"
            class="detail-section"
          >
            <h6 class="section-title">
              <span class="section-icon section-icon--theme">
                <i class="fa-solid fa-layer-group" />
              </span>
              <span class="flex-grow-1">{{ $ln(attribute) }}</span>
              <span class="section-count">{{ attribute.options.length }}</span>
            </h6>
            <div class="d-flex flex-wrap gap-2">
              <template
                v-for="option in attribute.options"
                :key="'opt-' + option.id"
              >
                <button
                  v-if="isAttributeOptionAvailable(attribute.id, option.id)"
                  type="button"
                  class="attr-btn"
                  :class="{ 'attr-btn--active': attributeSelections[attribute.id] === option.id }"
                  @click="selectAttributeOption(attribute.id, option.id)"
                >
                  <i
                    v-if="attributeSelections[attribute.id] === option.id"
                    class="fa-solid fa-check me-1"
                  />
                  {{ $ln(option) }}
                </button>
              </template>
            </div>
          </section>

          <!-- Modifier groups (interactive) -->
          <section
            v-for="modifier in activeModifiers"
            :key="'mod-' + modifier.id"
            class="detail-section"
          >
            <div class="modifier-card">
              <div class="modifier-header">
                <span class="section-icon section-icon--theme">
                  <i class="fa-solid fa-sliders" />
                </span>
                <div class="flex-grow-1" style="min-width: 0;">
                  <div class="fw-bold text-dark text-break">{{ $ln(modifier) }}</div>
                  <div class="small text-muted">{{ modifierRuleLabel(modifier) }}</div>
                </div>
                <span
                  v-if="modifier.is_required"
                  class="badge bg-danger bg-opacity-10 text-danger fw-semibold"
                >
                  <i class="fa-solid fa-asterisk me-1" style="font-size: 0.55rem;" />
                  {{ $t('Required') }}
                </span>
                <span
                  v-else
                  class="badge bg-secondary bg-opacity-10 text-secondary fw-semibold"
                >
                  {{ $t('Optional') }}
                </span>
              </div>

              <!-- Single select -->
              <template v-if="!modifier.allow_multiple">
                <label
                  v-for="option in modifier.options"
                  :key="'opt-' + option.id"
                  class="modifier-option"
                  :class="{
                    'modifier-option--selected': modifierSelections[modifier.id] === option.id,
                    'modifier-option--default': option.is_default && modifierSelections[modifier.id] !== option.id,
                  }"
                >
                  <input
                    type="radio"
                    class="form-check-input m-0 flex-shrink-0"
                    :name="'modifier-' + modifier.id"
                    :value="option.id"
                    v-model="modifierSelections[modifier.id]"
                  >
                  <span class="flex-grow-1 text-break">
                    {{ $ln(option) }}
                  </span>
                  <span v-if="option.is_default" class="default-badge">
                    <i class="fa-solid fa-star me-1" />{{ $t('Default') }}
                  </span>
                  <span class="option-adjustment" :class="priceAdjustmentClass(option.price_adjustment)">
                    {{ priceAdjustmentLabel(option.price_adjustment) }}
                  </span>
                </label>
              </template>

              <!-- Multi select -->
              <template v-else>
                <label
                  v-for="option in modifier.options"
                  :key="'opt-' + option.id"
                  class="modifier-option"
                  :class="{
                    'modifier-option--selected': (modifierSelections[modifier.id] || []).includes(option.id),
                    'modifier-option--default': option.is_default && !(modifierSelections[modifier.id] || []).includes(option.id),
                  }"
                >
                  <input
                    type="checkbox"
                    class="form-check-input m-0 flex-shrink-0"
                    :value="option.id"
                    :checked="(modifierSelections[modifier.id] || []).includes(option.id)"
                    @change="toggleMultiOption(modifier, option.id)"
                    :disabled="
                      modifier.max_options > 0
                        && (modifierSelections[modifier.id] || []).length >= modifier.max_options
                        && !(modifierSelections[modifier.id] || []).includes(option.id)
                    "
                  >
                  <span class="flex-grow-1 text-break">
                    {{ $ln(option) }}
                  </span>
                  <span v-if="option.is_default" class="default-badge">
                    <i class="fa-solid fa-star me-1" />{{ $t('Default') }}
                  </span>
                  <span class="option-adjustment" :class="priceAdjustmentClass(option.price_adjustment)">
                    {{ priceAdjustmentLabel(option.price_adjustment) }}
                  </span>
                </label>
              </template>
            </div>
          </section>

          <!-- Empty state: Simple product with no modifiers/attributes -->
          <div
            v-if="!attributes.length && !activeModifiers.length"
            class="text-center text-muted py-3"
          >
            <i class="fa-solid fa-mug-saucer fa-2x mb-2 opacity-50" />
            <p class="mb-0 small">{{ $t('No additional options for this item.') }}</p>
          </div>
        </div>
      </template>
    </div>

    <!--
      Sticky footer with the live unit price. Lives OUTSIDE the scroll
      container so it never moves and always sits flush against the
      bottom edge (which addresses the "edge isn't reaching" issue —
      the scrollable region above is bounded by the footer's top edge).
    -->
    <div v-if="!loading && !error" class="sheet-footer">
      <div class="d-flex align-items-center gap-3">
        <div class="flex-grow-1" style="min-width: 0;">
          <div class="footer-label text-truncate">
            <i class="fa-solid fa-eye me-1" />{{ $t('Current price') }}
          </div>
          <div class="footer-variant text-truncate">{{ $ln(displaySource) }}</div>
        </div>
        <div class="footer-price">
          <span v-if="compareUnitPrice" class="text-decoration-line-through opacity-50 me-2" style="font-size: 0.8rem;">{{ $storeIndex.formatThaiCurrency(compareUnitPrice) }}</span>
          {{ $storeIndex.formatThaiCurrency(currentUnitPrice) }}
        </div>
      </div>
      <div class="footer-note">
        <i class="fa-solid fa-circle-info me-1" />
        {{ $t('Preview only — please order at the counter or your table.') }}
      </div>
    </div>
  </div>
</template>

<script>
import { PreviewProducts } from '@/services/endpoint/preview.service';
import { useScrollRestore } from '@/composables/useScrollRestore';

/**
 * The preview detail API wraps relation collections in `{ data: [...] }`
 * envelopes (attributes.data, variants.data, modifiers.data, options.data).
 * Flatten them once on load so the template can treat everything as plain
 * arrays — matches the shape used by the public list response.
 */
function unwrapList(node) {
  if (Array.isArray(node)) return node;
  if (node && Array.isArray(node.data)) return node.data;
  return [];
}

function normalizeDetail(raw) {
  const d = raw || {};
  const attributes = unwrapList(d.attributes).map(a => ({
    ...a,
    options: unwrapList(a.options),
  }));
  const modifiers = unwrapList(d.modifiers).map(m => ({
    ...m,
    options: unwrapList(m.options),
  }));
  const variants = unwrapList(d.variants).map(v => ({
    ...v,
    modifiers: unwrapList(v.modifiers).map(m => ({
      ...m,
      options: unwrapList(m.options),
    })),
  }));
  return { ...d, attributes, variants, modifiers };
}

export default {
  name: 'PreviewProductDetailSheet',
  props: {
    product: { type: Object, required: true },
  },
  emits: ['close'],
  data() {
    return {
      loading: true,
      error: '',
      stub: { name: this.product.name, name_en: this.product.name_en },
      detail: {
        id: null,
        name: '',
        name_en: '',
        type: 'Simple',
        price: '0.00',
        image_url: null,
        thumbnail_url: null,
        category: '',
        attributes: [],
        variants: [],
        modifiers: [],
      },
      // --- Interactive selection state ----------------------------------
      // Mirrors service/ProductOptionsSheet — the SAME selection logic so
      // visitors can see exactly what a given attribute/modifier combo
      // would cost, just without the cart submission at the end.
      attributeSelections: {},   // { [attributeId]: optionId }
      modifierSelections: {},    // { [modifierId]: optionId | optionId[] }

      // --- Scroll progress (0..1) ---------------------------------------
      // Drives the smooth fade of the sticky bar (bg, blur, title fade-in)
      // and a subtle hero parallax. Updating on every scroll tick used to
      // pop the bar in/out abruptly; binding bar opacity to a continuous
      // ratio makes the transition feel native.
      scrollProgress: 0,
      scrollTop: 0,
      _rafPending: false,

      // --- Drag-to-resize / dismiss -------------------------------------
      // dragOffset is the sheet's translateY in px: 0 = fully expanded,
      // larger = dragged down. On release it snaps back to full or, if
      // dragged down far enough, slides out and emits `close`. dragging=true
      // disables the snap transition so the sheet tracks the finger 1:1.
      dragging: false,
      dragOffset: 0,
      _wrapH: 0,
      _dragStartY: 0,
      _dragStartOffset: 0,
      _onDragMove: null,
      _onDragEnd: null,
      _onResizeWrap: null,
    };
  },
  computed: {
    attributes() {
      return this.detail.attributes || [];
    },
    variants() {
      return this.detail.variants || [];
    },
    /**
     * Selected variant only matters for Configurable products. It's the
     * variant whose option_ids exactly match the user's full attribute
     * selection. Returns null until every attribute has been chosen.
     */
    selectedVariant() {
      if (!this.variants.length || !this.attributes.length) return null;
      const selectedIds = Object.values(this.attributeSelections);
      if (selectedIds.length !== this.attributes.length) return null;
      return this.variants.find(v => {
        if (!v.option_ids || v.option_ids.length !== selectedIds.length) return false;
        return selectedIds.every(id => v.option_ids.includes(id));
      }) || null;
    },
    // คืน object ไม่ใช่ string เพื่อให้ $ln เลือกภาษาได้ (ต้องเห็นทั้ง name และ name_en)
    displaySource() {
      return this.selectedVariant ? this.selectedVariant : this.detail;
    },
    /**
     * Modifier source: variant-level when a Configurable product has a
     * variant picked, otherwise product-level (Simple or pre-selection).
     */
    activeModifiers() {
      if (this.selectedVariant) return this.selectedVariant.modifiers || [];
      return this.detail.modifiers || [];
    },
    totalModifierCount() {
      return this.activeModifiers.reduce((s, m) => s + (m.options?.length || 0), 0);
    },
    basePrice() {
      if (this.selectedVariant) return parseFloat(this.selectedVariant.price) || 0;
      return parseFloat(this.detail.price) || 0;
    },
    modifierPriceAdjustment() {
      let adj = 0;
      for (const modifier of this.activeModifiers) {
        const sel = this.modifierSelections[modifier.id];
        if (!sel && sel !== 0) continue;
        if (modifier.allow_multiple && Array.isArray(sel)) {
          for (const optId of sel) {
            const opt = modifier.options.find(o => o.id === optId);
            if (opt) adj += parseFloat(opt.price_adjustment) || 0;
          }
        } else {
          const opt = modifier.options.find(o => o.id === sel);
          if (opt) adj += parseFloat(opt.price_adjustment) || 0;
        }
      }
      return adj;
    },
    /** Live unit price (base + modifier adjustments) shown in footer. */
    currentUnitPrice() {
      return this.basePrice + this.modifierPriceAdjustment;
    },
    /**
     * Inline style driving the sticky bar's bg + blur from the scroll
     * progress (0..1). Avoids the abrupt jump that the old binary
     * is-scrolled toggle produced once you crossed the 180px threshold.
     */
    barStyle() {
      const p = this.scrollProgress;
      const blur = `blur(${10 * p}px)`;
      return {
        background: `rgba(255, 255, 255, ${0.95 * p})`,
        backdropFilter: blur,
        // Capital W so Vue emits `-webkit-backdrop-filter`; lowercase
        // would produce the non-standard `webkit-backdrop-filter`.
        WebkitBackdropFilter: blur,
        boxShadow: p > 0.7 ? '0 1px 0 rgba(0, 0, 0, 0.08)' : 'none',
      };
    },
    /**
     * Close button swaps from semi-transparent dark (over the hero) to
     * neutral grey (over the white bar) as the bar fades in. Interpolated
     * so the colour transition happens smoothly alongside the bg fade.
     */
    barBtnStyle() {
      const p = this.scrollProgress;
      // Cross-fade two background colors:
      //  • At p=0 (no scroll): black 50% over hero
      //  • At p=1 (scrolled):  neutral light grey
      const darkA = 0.5 * (1 - p);
      const lightA = p;
      return {
        background: p < 0.5
          ? `rgba(0, 0, 0, ${darkA})`
          : `rgba(241, 243, 245, ${lightA})`,
        color: p > 0.5 ? '#333' : '#fff',
      };
    },
    /**
     * Title text fades in from progress 0.4 onward (slightly after the
     * bar background starts appearing) so the text doesn't compete with
     * the still-visible hero title behind it.
     */
    barTitleStyle() {
      const p = this.scrollProgress;
      const titleOpacity = Math.max(0, (p - 0.4) / 0.6);
      return {
        opacity: titleOpacity,
        transform: `translateY(${(1 - titleOpacity) * 4}px)`,
      };
    },
    /**
     * Subtle hero parallax — image moves at ~40% scroll speed so the
     * photo "stays" briefly while content scrolls over it. Capped so
     * the image doesn't leave its container empty at the bottom.
     */
    heroParallaxStyle() {
      const offset = Math.min(120, this.scrollTop * 0.4);
      return {
        transform: `translate3d(0, ${offset}px, 0)`,
      };
    },
    /**
     * Hero price label: min–max range across variants if Configurable,
     * otherwise the flat product price.
     */
    priceRangeLabel() {
      const fmt = this.$storeIndex.formatThaiCurrency;
      if (this.variants.length) {
        const prices = this.variants
          .map(v => parseFloat(v.price))
          .filter(n => !isNaN(n));
        if (prices.length) {
          const min = Math.min(...prices);
          const max = Math.max(...prices);
          return min === max ? fmt(min) : `${fmt(min)} – ${fmt(max)}`;
        }
      }
      return fmt(parseFloat(this.detail.price) || 0);
    },
    /**
     * ราคาเต็ม (compare_at_price) สำหรับขีดฆ่าข้างราคาจริง — โชว์เฉพาะสินค้า
     * flat price (ไม่มี variant) และเมื่อราคาเต็มสูงกว่าราคาขายจริง
     */
    compareAtLabel() {
      if (this.variants.length) return '';
      const compare = Number(this.detail.compare_at_price);
      const flat = parseFloat(this.detail.price);
      if (!isNaN(compare) && !isNaN(flat) && flat > 0 && compare > flat) {
        return this.$storeIndex.formatThaiCurrency(compare);
      }
      return '';
    },
    /**
     * ราคาเต็มของ variant ที่เลือก (รวมส่วนปรับจาก modifier) สำหรับขีดฆ่าใน footer
     * — ใช้กับสินค้า config; สินค้า flat ใช้ compareAtLabel ที่ hero แทน
     */
    compareUnitPrice() {
      if (!this.selectedVariant) return 0;
      const compare = Number(this.selectedVariant.compare_at_price);
      const base = parseFloat(this.selectedVariant.price);
      if (isNaN(compare) || isNaN(base) || compare <= base) return 0;
      return compare + (this.currentUnitPrice - base);
    },
    /**
     * Sheet transform. While dragging we drop the transition so it tracks
     * the finger; on release the spring-ish cubic-bezier eases it to its
     * snap point (or off-screen on dismiss).
     */
    wrapStyle() {
      return {
        transform: `translateY(${this.dragOffset}px)`,
        transition: this.dragging
          ? 'none'
          : 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
      };
    },
  },
  watch: {
    selectedVariant(newVal, oldVal) {
      // When the variant changes, drop modifier state and reseed defaults
      // because variant-scoped modifiers can be entirely different.
      if (newVal?.id !== oldVal?.id) {
        this.modifierSelections = {};
        this.$nextTick(() => this.initDefaultModifiers());
      }
    },
  },
  mounted() {
    this.fetchInfo();
    // Lock background scroll while the sheet is open.
    document.body.style.overflow = 'hidden';
    useScrollRestore(() => this.$refs.sheet);

    this.$nextTick(() => this.measureWrap());
    this._onResizeWrap = () => this.measureWrap();
    window.addEventListener('resize', this._onResizeWrap);
  },
  beforeUnmount() {
    document.body.style.overflow = '';
    if (this._onResizeWrap) window.removeEventListener('resize', this._onResizeWrap);
    // Drop any drag listeners still attached if we unmount mid-gesture.
    this.detachDragListeners();
  },
  methods: {
    /**
     * Map raw scrollTop to a 0..1 progress between two anchors:
     *   • 40px  — when the bar starts to materialise
     *   • 200px — fully opaque (hero is well out of frame)
     * rAF-throttled so a fast scroll burst produces one paint per frame
     * instead of one per event — keeps the interpolation smooth even
     * with many wheel/touch events per second.
     */
    onSheetScroll(ev) {
      this.scrollTop = ev.target.scrollTop;
      if (this._rafPending) return;
      this._rafPending = true;
      requestAnimationFrame(() => {
        this._rafPending = false;
        const top = this.scrollTop;
        const start = 40;
        const end = 200;
        const ratio = Math.min(1, Math.max(0, (top - start) / (end - start)));
        this.scrollProgress = ratio;
      });
    },

    // --- Drag-to-resize / dismiss ---------------------------------------
    /** Cache the sheet's rendered height — snap thresholds are relative to it. */
    measureWrap() {
      const w = this.$refs.sheetWrap;
      this._wrapH = w ? w.offsetHeight : Math.round(window.innerHeight * 0.92);
    },
    onHandleDown(e) {
      // Tapping the close button must still close — don't hijack it as a drag.
      if (e.target.closest('.bar-btn')) return;
      this.measureWrap();
      this.dragging = true;
      this._dragStartY = e.clientY;
      this._dragStartOffset = this.dragOffset;
      this._onDragMove = ev => this.onDragMove(ev);
      this._onDragEnd = () => this.onDragEnd();
      window.addEventListener('pointermove', this._onDragMove, { passive: false });
      window.addEventListener('pointerup', this._onDragEnd);
      window.addEventListener('pointercancel', this._onDragEnd);
      e.preventDefault();
    },
    onDragMove(e) {
      let next = this._dragStartOffset + (e.clientY - this._dragStartY);
      // Rubber-band when pulled above the fully-expanded position so the
      // sheet resists rather than floating up off its bottom anchor.
      if (next < 0) next *= 0.3;
      this.dragOffset = next;
      if (e.cancelable) e.preventDefault();
    },
    onDragEnd() {
      this.detachDragListeners();
      this.dragging = false;
      const h = this._wrapH || window.innerHeight;
      // Two states only: dragged down far enough → dismiss, otherwise
      // snap back fully expanded. No middle/peek resting point.
      if (this.dragOffset > h * 0.3) this.dismiss();
      else this.dragOffset = 0;
    },
    detachDragListeners() {
      if (this._onDragMove) window.removeEventListener('pointermove', this._onDragMove);
      if (this._onDragEnd) {
        window.removeEventListener('pointerup', this._onDragEnd);
        window.removeEventListener('pointercancel', this._onDragEnd);
      }
      this._onDragMove = null;
      this._onDragEnd = null;
    },
    /** Slide the sheet out, then let the parent unmount it. */
    dismiss() {
      this.dragOffset = (this._wrapH || window.innerHeight) + 40;
      setTimeout(() => this.$emit('close'), 260);
    },

    async fetchInfo() {
      if (!this.product?.id) {
        this.error = this.$t('Missing product');
        this.loading = false;
        return;
      }
      this.loading = true;
      this.error = '';
      try {
        const res = await PreviewProducts.info(this.product.id);
        const raw = res.data?.data ?? res.data;
        this.detail = normalizeDetail(raw);
        this.initDefaultAttributes();
        this.$nextTick(() => this.initDefaultModifiers());
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || this.$t('Network error');
      } finally {
        this.loading = false;
      }
    },

    // --- Attribute / variant helpers ------------------------------------
    /**
     * An option is "available" if at least one variant in the catalogue
     * includes it given the user's other selections. Hide dead
     * combinations so visitors can't pick a non-existent variant.
     */
    isAttributeOptionAvailable(attributeId, optionId) {
      const test = { ...this.attributeSelections, [attributeId]: optionId };
      const ids = Object.values(test);
      return this.variants.some(v => ids.every(id => v.option_ids?.includes(id)));
    },
    selectAttributeOption(attributeId, optionId) {
      this.attributeSelections = { ...this.attributeSelections, [attributeId]: optionId };
    },
    /** Seed selections from the first in-stock variant so visitors land
        on a valid combo right away rather than an empty form. */
    initDefaultAttributes() {
      if (!this.attributes.length || !this.variants.length) return;
      const first = this.variants[0];
      if (!first?.option_ids) return;
      const sel = {};
      for (const attr of this.attributes) {
        const matched = attr.options.find(o => first.option_ids.includes(o.id));
        if (matched) sel[attr.id] = matched.id;
      }
      this.attributeSelections = sel;
    },

    // --- Modifier helpers ----------------------------------------------
    toggleMultiOption(modifier, optionId) {
      const current = [...(this.modifierSelections[modifier.id] || [])];
      const idx = current.indexOf(optionId);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        if (modifier.max_options > 0 && current.length >= modifier.max_options) return;
        current.push(optionId);
      }
      this.modifierSelections = { ...this.modifierSelections, [modifier.id]: current };
    },
    initDefaultModifiers() {
      const sel = {};
      for (const modifier of this.activeModifiers) {
        const defaults = modifier.options.filter(o => o.is_default);
        if (!defaults.length) continue;
        if (modifier.allow_multiple) sel[modifier.id] = defaults.map(o => o.id);
        else sel[modifier.id] = defaults[0].id;
      }
      this.modifierSelections = sel;
    },

    // --- Display helpers ------------------------------------------------
    modifierRuleLabel(modifier) {
      if (modifier.allow_multiple) {
        if (modifier.max_options > 0) {
          return this.$t('Select') + ' ' + modifier.min_options + '-' + modifier.max_options;
        }
        return this.$t('Choose multiple');
      }
      return this.$t('Choose one');
    },
    priceAdjustmentLabel(raw) {
      const n = parseFloat(raw);
      if (isNaN(n) || n === 0) return this.$t('Free');
      const fmt = this.$storeIndex.formatThaiCurrency;
      return n > 0 ? '+' + fmt(n) : fmt(n);
    },
    priceAdjustmentClass(raw) {
      const n = parseFloat(raw);
      if (isNaN(n) || n === 0) return 'text-muted';
      return n > 0 ? 'text-success' : 'text-danger';
    },
  },
};
</script>

<style scoped>
/*
  Two-layer wrapper. The outer .sheet-wrap owns positioning + the
  rounded corners + bottom anchor, with overflow:hidden so the rounded
  top corners crisply clip the inner scroller and the sticky footer.
  The inner .sheet is the actual scroll container — this split fixes
  the "edge isn't reaching" issue where the sticky footer would clip
  oddly against the rounded corners and the bottom of the scroll area
  showed backdrop instead of the sheet background.
*/
.sheet-wrap {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1055;
  max-height: 92vh;
  max-height: 92dvh;
  border-radius: 18px 18px 0 0;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}
.sheet {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  /*
    Keep rubber-band scroll inside the sheet — without this, swiping
    past the top/bottom would scroll the page behind, giving the sheet
    a "loose" feel. `contain` is exactly what we want here: scroll
    chaining stops at the sheet boundary.
  */
  overscroll-behavior: contain;
}

/* --- Drag handle ------------------------------------------------------- */
/*
  Centred grabber that sits above everything (z > sticky bar) so it stays
  visible wherever the user has scrolled. Kept narrow + centred so it
  never covers the close button on the left. touch-action:none stops a
  drag here from being interpreted as a scroll.
*/
.drag-handle {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 90px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 6;
  cursor: grab;
  touch-action: none;
}
.drag-handle:active { cursor: grabbing; }
.drag-handle__bar {
  width: 40px;
  height: 4px;
  border-radius: 999px;
  /* Light fill with a faint dark outline so it reads on both the dark
     hero photo and the white sticky bar once scrolled. */
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.25);
}

/* --- Sticky top bar --------------------------------------------------- */
/*
  bg / blur / button colour / title fade are all driven from inline
  styles bound to a single scrollProgress (0..1) ratio in JS. Doing
  this in JS rather than via CSS classes lets the transition feel
  continuous as the user scrolls — no perceptible jump at any one
  threshold. Inline styles are rAF-throttled in onSheetScroll.
*/
.sticky-bar {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  z-index: 5;
  will-change: background, backdrop-filter;
  /* The bar is the drag handle: grab cursor + touch-action:none so a drag
     here moves the sheet instead of scrolling the content. The close
     button re-enables its own pointer cursor below. */
  cursor: grab;
  touch-action: none;
}
.sticky-bar:active { cursor: grabbing; }
.sticky-bar .bar-btn { cursor: pointer; }
.sticky-bar .bar-btn {
  width: 38px;
  height: 38px;
  border-radius: 999px;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
  /*
    Snappy on hover only — bg/colour during scroll are driven by inline
    style so the JS-controlled interpolation isn't fighting a CSS
    transition each frame.
  */
}
.sticky-bar .bar-btn:hover { filter: brightness(0.92); }
.sticky-bar .bar-title {
  flex-grow: 1;
  min-width: 0;
  font-size: 0.95rem;
  color: #1d1d1d;
  /* opacity/transform driven by barTitleStyle inline. */
}
.sticky-bar .bar-price {
  color: #d46010;
  font-weight: 800;
  font-size: 0.85rem;
}
.sticky-bar .bar-spacer {
  width: 38px;
  flex-shrink: 0;
}

/* --- Hero -------------------------------------------------------------- */
.hero {
  position: relative;
  height: 280px;
  background: #1d1d1d;
  margin-top: -54px;
  overflow: hidden;
  /*
    Explicit top-corner radius on the hero so the rounded look is
    guaranteed regardless of the parent's overflow-clip behaviour.
    .sheet-wrap also has overflow:hidden + border-radius, but a few
    mobile WebKit builds don't reliably propagate that clip through
    a nested scroll container — declaring it here is defensive.
  */
  border-top-left-radius: 18px;
  border-top-right-radius: 18px;
}
@media (max-width: 360px) { .hero { height: 220px; } }
.hero-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /*
    Promoted to its own layer so the parallax transform is GPU-driven
    — without will-change the scroll-tied transform can stutter on
    lower-end Android. Bounded `transform` updates (Math.min(120, ...))
    keep the image inside the hero so no empty area appears underneath.
  */
  will-change: transform;
}
.hero-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e96c15 0%, #ff8a3c 100%);
  color: rgba(255, 255, 255, 0.85);
  font-size: 4.5rem;
}
.hero-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.45) 0%,
    rgba(0, 0, 0, 0) 25%,
    rgba(0, 0, 0, 0) 45%,
    rgba(0, 0, 0, 0.75) 100%
  );
}
.hero-category {
  position: absolute;
  top: 60px;
  left: 0.85rem;
  background: rgba(255, 255, 255, 0.95);
  color: #d46010;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  z-index: 2;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}
.hero-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 1rem 1.15rem 1.15rem;
  z-index: 2;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
}
.hero-title {
  font-size: 1.45rem;
  line-height: 1.2;
}
.hero-price {
  background: #fff;
  color: #d46010;
  font-weight: 800;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  font-size: .8rem;
  text-shadow: none;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
}
.hero-meta-chip {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: #fff;
  font-size: 0.72rem;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  font-weight: 600;
  text-shadow: none;
  backdrop-filter: blur(4px);
}
.hero-skeleton {
  height: 280px;
  border-radius: 0;
}

/* --- Content ----------------------------------------------------------- */
.preview-notice {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.85rem 1rem;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(233, 108, 21, 0.08), rgba(255, 138, 60, 0.08));
  border: 1px solid rgba(233, 108, 21, 0.18);
  color: #6d3a10;
}
.preview-notice__icon {
  width: 38px;
  height: 38px;
  border-radius: 999px;
  background: #fff;
  color: #d46010;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
  box-shadow: 0 3px 8px rgba(233, 108, 21, 0.18);
}

.detail-section { margin-bottom: 1.5rem; }
.section-title {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  font-weight: 700;
  margin-bottom: 0.85rem;
  color: #1d1d1d;
  font-size: 1rem;
}
.section-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;
  margin-inline-end: 0.85rem;
}
.section-title .section-icon { margin-inline-end: 0; }
.section-icon--theme {
  background: rgba(233, 108, 21, 0.12);
  color: #d46010;
}
.section-count {
  background: #f1f3f5;
  color: #6c757d;
  font-size: 0.7rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-weight: 600;
}

/* --- Interactive attribute buttons ------------------------------------ */
.attr-btn {
  background: #fff;
  border: 1.5px solid #dee2e6;
  color: #1d1d1d;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-size: 0.88rem;
  font-weight: 600;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.1s ease;
  cursor: pointer;
}
.attr-btn:hover { border-color: #ffb380; }
.attr-btn:active { transform: scale(0.97); }
.attr-btn--active {
  background: #e96c15;
  border-color: #e96c15;
  color: #fff;
  box-shadow: 0 4px 10px rgba(233, 108, 21, 0.3);
}
.attr-btn--active:hover { border-color: #d46010; background: #d46010; }

/* --- Modifier group ---------------------------------------------------- */
.modifier-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 16px;
  overflow: hidden;
  /* `pb` provides breathing room below the last option chip — the
     chips have margin-top, no margin-bottom, so without this the
     last chip would butt right up against the card edge. */
  padding-bottom: 0.6rem;
}
.modifier-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  background: #fafbfc;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 0.2rem;
}
/*
  Each option is its own rounded chip with a transparent border by
  default. Hover / default / selected all swap the border colour and
  bg — same chip shape, just different states — so the "rounded" look
  is consistent across every interactive state.
*/
.modifier-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  font-size: 0.92rem;
  color: #2b2b2b;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  margin: 0.4rem 0.6rem 0;
  border: 1.5px solid transparent;
  border-radius: 12px;
  background: #fafbfc;
}
.modifier-option:hover {
  border-color: #ffb380;
  background: #fff;
}
.modifier-option--default {
  border-color: rgba(255, 193, 7, 0.4);
  background: rgba(255, 193, 7, 0.06);
}
.modifier-option--selected {
  border-color: #e96c15;
  background: rgba(233, 108, 21, 0.1);
  box-shadow: 0 4px 10px rgba(233, 108, 21, 0.12);
}
.modifier-option .form-check-input:checked {
  background-color: #e96c15;
  border-color: #e96c15;
}
.modifier-option .form-check-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(233, 108, 21, 0.2);
  border-color: #e96c15;
}
.default-badge {
  background: rgba(255, 193, 7, 0.18);
  color: #b58300;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
}
.option-adjustment {
  font-weight: 700;
  font-size: 0.82rem;
  white-space: nowrap;
  margin-left: auto;
}

/* --- Sticky footer (live price, outside scroll container) ------------- */
.sheet-footer {
  flex-shrink: 0;
  background: #fff;
  border-top: 1px solid #e9ecef;
  padding: 0.85rem 1rem calc(0.65rem + env(safe-area-inset-bottom, 0));
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
}
.footer-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
}
.footer-variant {
  font-size: 1rem;
  font-weight: 700;
  color: #1d1d1d;
}
.footer-price {
  background: linear-gradient(135deg, #e96c15 0%, #ff8a3c 100%);
  color: #fff;
  font-weight: 800;
  font-size: 1.2rem;
  padding: 0.55rem 1.1rem;
  border-radius: 14px;
  box-shadow: 0 6px 14px rgba(233, 108, 21, 0.3);
  white-space: nowrap;
}
.footer-note {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #eef0f2;
  font-size: 0.72rem;
  color: #6c757d;
  text-align: center;
}

/* --- Skeleton ---------------------------------------------------------- */
.skeleton-shimmer {
  background: linear-gradient(90deg, #eceff2 0%, #f5f7f9 50%, #eceff2 100%);
  background-size: 200% 100%;
  animation: pos-skeleton-shimmer 1.25s ease-in-out infinite;
  display: block;
}
@keyframes pos-skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
